import { Button } from '@daily/shared/components/Button';
import { Card, CardContent } from '@daily/shared/components/Card';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { ThemeScope, useTheme } from '@daily/shared/contexts/Theme';
import {
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakouts } from '../../contexts/BreakoutsProvider';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useStyleVariants } from '../../hooks/useStyleVariants';
import { SIDEBAR_WIDTH } from '../../lib/constants';
import { robotsClassName } from '../../lib/robots';
import { useIsMobile, useSidebarView } from '../../lib/state/core';
import { useBreakoutSessionState } from './hooks/useBreakoutSessionState';

export const BreakoutHostHelpNotification: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const { mediaQueries } = useTheme();
  const { textVariantStrong } = useStyleVariants();
  const [isMobile] = useIsMobile();
  const [sidebarView, setSidebarView] = useSidebarView();
  const [showNotification, setShowNotification] = useState(false);

  const { breakoutMapping, participantsAskingForHelp } =
    useBreakoutSessionState();
  const { moveParticipants } = useBreakouts();

  const localSessionId = useLocalSessionId();

  const roomsWithRequests = Object.entries(breakoutMapping)
    .filter(([, room]) =>
      room.users.some((u) => participantsAskingForHelp.includes(u))
    )
    .map(([roomId, room]) => ({
      ...room,
      id: roomId,
    }));

  const firstUserName =
    useParticipantProperty(participantsAskingForHelp?.[0], 'user_name') ||
    t('people.guest');

  const handleViewAllClick = useCallback(() => {
    setSidebarView('people');
    setShowNotification(false);
  }, [setSidebarView]);
  const handleJoinRoomClick = useCallback(
    (id: string) => {
      moveParticipants({
        [localSessionId]: id,
      });
      setShowNotification(false);
    },
    [localSessionId, moveParticipants]
  );
  const handleDismissClick = useCallback(() => {
    setShowNotification(false);
  }, []);

  const prevParticipantsAskingForHelp = usePreviousValue(
    participantsAskingForHelp
  );
  useEffect(() => {
    if (
      participantsAskingForHelp.length > prevParticipantsAskingForHelp?.length
    ) {
      setShowNotification(true);
    }
  }, [participantsAskingForHelp.length, prevParticipantsAskingForHelp?.length]);

  if (!showNotification || !roomsWithRequests.length) return null;

  const firstRoom = roomsWithRequests[0];

  return (
    <div className="breakout-host-help-notification">
      <ThemeScope theme="light">
        <div className="backdrop" role="presentation" />
        <div
          className={classnames('notification-stack', {
            'with-sidebar': Boolean(sidebarView),
          })}
        >
          <Card noBorder spacing={8} variant="small">
            <CardContent>
              <Stack>
                <Text variant={textVariantStrong}>
                  {roomsWithRequests.length > 1
                    ? t('breakouts.multipleRoomsAskingForHelp')
                    : participantsAskingForHelp.length > 1
                    ? t('breakouts.usersAskingForHelp', {
                        count: participantsAskingForHelp.length,
                      })
                    : t('breakouts.userAskingForHelp', {
                        name: firstUserName,
                      })}
                </Text>
                <Stack align="center" gap={isMobile ? 16 : 4} horizontal>
                  {roomsWithRequests.length === 1 ? (
                    <Button
                      id={`breakouts-help-notification-join-${firstRoom.name}`}
                      className={classnames(
                        robotsClassName(`btn-br-join-room-${firstRoom.name}`)
                      )}
                      onClick={() => handleJoinRoomClick(firstRoom.id)}
                      size="small"
                      variant="primary"
                    >
                      {t('breakouts.joinRoomWithName', {
                        name: t('breakouts.roomWithName', {
                          name: firstRoom.name,
                        }),
                      })}
                    </Button>
                  ) : (
                    <Button
                      id="breakouts-help-notification-view-all"
                      className={robotsClassName('btn-br-help-view-all')}
                      onClick={handleViewAllClick}
                      size="small"
                      variant="primary"
                    >
                      {t('breakouts.viewAll')}
                    </Button>
                  )}
                  <Button
                    id={`breakouts-help-notification-dismiss`}
                    className={robotsClassName(`btn-br-dismiss`)}
                    onClick={handleDismissClick}
                    size="small"
                    variant="secondary"
                  >
                    {t('general.dismiss')}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </div>
      </ThemeScope>
      <style jsx>{`
        .breakout-host-help-notification {
          height: 100%;
          position: absolute;
          width: 100%;
          z-index: var(--zindex-waitingroom);
        }
        .backdrop {
          background: linear-gradient(
            180deg,
            var(--backdrop) 0%,
            var(--backdrop-hidden) 100%
          );
          height: calc(100% - 59px);
          left: 0;
          position: absolute;
          top: var(--header-height);
          width: 100%;
        }
        .notification-stack {
          align-items: flex-end;
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: absolute;
          right: 8px;
          top: calc(var(--header-height) + 8px);
        }
        .notification-stack.with-sidebar {
          right: ${Number(SIDEBAR_WIDTH) + 8}px;
        }
        .notification-stack :global(.card-content) {
          padding-right: 24px;
          position: relative;
        }
        @media ${mediaQueries.coarse} {
          .notification-stack :global(.card-content) {
            padding-right: 32px;
          }
        }
      `}</style>
    </div>
  );
};
